import useGoodsByMenu, {GoodsByMenu} from "@QUERIES/prdt/goods/useGoodsByMenu";
import {useAppDispatch, useAppSelector} from "@STORE/index";
import {setGoodsFilter} from "@STORE/store-filter";
import {useRouter} from "next/router";
import {firstBy} from "thenby";

type Filter = {key: string; text: string; value: string; asc?: boolean};
/* filteringProduct */
const filterUtils = (productList: Array<GoodsByMenu>, filterList: Array<Filter>) => {
	if (!productList) return;
	let filteringData = productList;

	const filtering = () => {
		filterList?.forEach(item => {
			const filterKey = item.key;
			if (["sort"].indexOf(filterKey) > -1) return;
			filteringData = filteringData.reduce((acc, product) => {
				const filterKeyList = filterList.filter(v => v.key === filterKey);
				filterKeyList.some(filter => {
					const {key, value} = filter;
					if (key === "minPrice") return Number(product.minPrice) >= Number(value);
					if (key === "maxPrice") return Number(product.minPrice) <= Number(value);
					return product[key] && product[key].indexOf(value) > -1;
					// eslint-disable-next-line no-param-reassign
				}) && (acc = acc.concat(product));
				return acc;
			}, []);
		});
	};

	const sorting = () => {
		const getSortOrder = (dspSid: string, sortOrderInfo: string) => {
			if (!sortOrderInfo) return 99998;

			// 선택한 지역 (예: AAA0001,AAA0001,.....)
			const dspIdArray: Array<string> = dspSid.split(",");

			// 지역별 정렬 순서 정보 (예: AAA0001^1,AAA0001^2,.....)
			const sortOrderInfoArray = sortOrderInfo.split(",");

			// 선택한 지역들의 정렬 순서정보.
			const filteredSortArray = dspIdArray.reduce((acc, cur, idx, arr) => {
				sortOrderInfoArray.forEach(orderInfo => {
					const orderInfoArray = orderInfo.split("^");
					orderInfoArray[0] === cur && acc.push(Number(orderInfoArray[1]));
				});
				return acc;
			}, []);

			// sortOrderInfo가 없다면 맨뒤로 위치함.
			if (!filteredSortArray.length) return 99999;

			// 지역에 해당하는 sort 중 가장 작은 값.
			return Math.min.apply(null, filteredSortArray);
		};
		const sort = filterList?.find(v => v.key === "sort") || {asc: true, value: "sortOrder"};
		const {asc, value: prop} = sort;

		const dspSidArray = filterList?.filter(v => v.key === "dspSid") || [];
		const filterDspSid =
			dspSidArray.length > 0
				? dspSidArray.reduce((acc, item, index, array) => acc.concat(item.value), []).join(",")
				: "";
		const copyData = filteringData;
		if (prop === "sortOrder" && filterDspSid) {
			filteringData = copyData.sort(
				firstBy((a: GoodsByMenu, b: GoodsByMenu) => {
					const aVal = getSortOrder(filterDspSid as string, a.sortOrderInfo);
					const bVal = getSortOrder(filterDspSid as string, b.sortOrderInfo);
					// eslint-disable-next-line no-nested-ternary
					return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
					// eslint-disable-next-line no-nested-ternary
				}).thenBy((a, b) => (Number(a[prop]) > Number(b[prop]) ? 1 : Number(a[prop]) < Number(b[prop]) ? -1 : 0)),
			);
		} else {
			filteringData = copyData.sort((a, b) => {
				if (asc) {
					// eslint-disable-next-line no-nested-ternary
					return Number(a[prop]) > Number(b[prop]) ? 1 : Number(a[prop]) < Number(b[prop]) ? -1 : 0;
				} else {
					// eslint-disable-next-line no-nested-ternary
					return Number(b[prop]) > Number(a[prop]) ? 1 : Number(b[prop]) < Number(a[prop]) ? -1 : 0;
				}
			});
		}
	};
	const getFiltering = () => {
		filtering();
		sorting();
		return filteringData;
	};
	return {getFiltering};
};

const useFilterStore = () => {
	const router = useRouter();
	const dspSid = router.query?.dspSid as string;
	const filterStore = useAppSelector(state => state.goodsFilterStore);
	const dispatch = useAppDispatch();

	/** @description store 필터리스트 호출 */
	const getFilter = () => filterStore.goodsFilter && filterStore.goodsFilter[dspSid];

	/** @description store 모든 필터 데이터 변경(분기처리 사용) */
	const setFilter = arr => {
		const setArray = {
			[dspSid]: [...arr],
		};
		dispatch(setGoodsFilter(setArray));
	};

	/** @description store 필터 데이터 추가 */
	const addFilter = (key = "", value = "", text = "") => {
		const currentFilter = getFilter();
		const filterExists = currentFilter.some(item => item.value === value && item.key === key); /* 중복제거 */
		if (filterExists) return;

		const setArray = {
			[dspSid]: [...currentFilter, {key, value, text}],
		};
		dispatch(setGoodsFilter(setArray));
	};

	/** @description store 필터 데이터 제거 (value를 입력하지 않으면 모든 key를 제거합니다) */
	const removeFilter = (key = "", value = "") => {
		const currentFilter = getFilter();
		let filterArray = [];
		if (value !== "") {
			filterArray = currentFilter.filter(item => !(item.value === value && item.key === key)); /* 동일한 value 제거 */
		} else {
			filterArray = currentFilter.filter(item => item.key !== key); /* 모든 key 제거 */
		}
		const setArray = {
			[dspSid]: filterArray,
		};
		dispatch(setGoodsFilter(setArray));
	};

	const getFilteringProduct = () => {
		const productListFetch = useGoodsByMenu(dspSid);
		const productList = productListFetch.data?.body;
		const filterList = getFilter();
		if (productListFetch.isFetching) return [];
		const data = filterUtils(productList, filterList).getFiltering();
		return data;
	};

	return {getFilter, setFilter, addFilter, removeFilter, getFilteringProduct};
};

export default useFilterStore;
